import { memo, type ReactNode, useCallback } from 'react'
import { SvgComponent } from 'react-common'
import cn from 'classnames'
import { ActivityType } from '@/api/models/activity'
import { useMemoTranslation } from '@/hooks/useMemoTranslation'
import { CheckInCircle } from './CheckInCircle'
import { FailedTriangle } from './FailedTriangle'
import { NotStartedCircle } from './NotStartedCircle'
import { OptionalCircle } from './OptionalCircle'
import styles from './TOCMenuItemComponent.module.scss'
import { ActivityIcon } from '../ActivityIcon/ActivityIcon'
import { TOCMenuTooltip } from '../TOCMenuTooltip/TOCMenuTooltip'

type Props = {
  name: string
  code: string
  isDraft: boolean
  isLocked: boolean
  isCompleted: boolean
  isPassed?: boolean | null
  isOptional: boolean
  isProctored: boolean
  activityType: ActivityType
}

const IconWrapper = ({ children }: { children: ReactNode }) => <div className={styles.rightIcon}>{children}</div>

export const TOCMenuItemComponent = memo(
  ({ name, code, isDraft, isLocked, isCompleted, isPassed, isOptional, isProctored, activityType }: Props) => {
    const { t } = useMemoTranslation()

    const getRightIcon = useCallback((): JSX.Element | null => {
      if (isDraft) {
        return (
          <IconWrapper>
            <SvgComponent name="TimeBoldStroke" size={16} />
          </IconWrapper>
        )
      }

      if (isCompleted) {
        if (isPassed || isPassed == null) {
          return (
            <IconWrapper>
              <CheckInCircle />
            </IconWrapper>
          )
        }
        if (!isPassed) {
          return (
            <IconWrapper>
              <FailedTriangle />
            </IconWrapper>
          )
        }
      }

      if (isLocked) {
        return null
      }

      if (isOptional) {
        return (
          <IconWrapper>
            <OptionalCircle />
          </IconWrapper>
        )
      }

      return (
        <IconWrapper>
          <NotStartedCircle />
        </IconWrapper>
      )
    }, [isDraft, isOptional, isCompleted, isLocked, isPassed])

    const getTooltipMessage = useCallback((): string | undefined => {
      if (isDraft) return t('compositions.comingSoon')
      if (isCompleted) {
        if (isPassed || isPassed == null) return undefined
        if (!isPassed) return t('compositions.belowPassingScore')
      }
      if (isLocked) return undefined
      if (isOptional) return t('compositions.optional')
      if (activityType === ActivityType.lti && isProctored) return t('compositions.proctoredTooltip')

      return undefined
    }, [activityType, isCompleted, isDraft, isLocked, isOptional, isPassed, isProctored, t])

    const isGrayedOut = isDraft || isLocked
    const tooltipMessage = getTooltipMessage()

    return (
      <div
        className={cn(styles.wrapper, { [styles.grayedOut]: isGrayedOut })}
        data-tip={tooltipMessage}
        data-for={`toc tooltip ${code}`}
      >
        <ActivityIcon type={activityType} />
        {name}
        {getRightIcon()}
        {Boolean(tooltipMessage) && <TOCMenuTooltip code={code} />}
      </div>
    )
  },
)
